import { useEffect, useRef, useState } from "react";
import { useAuth } from "./context/UserContextProvider";
import LearnerApp from "./screens/learner/LearnerApp";
import TourProvider from "./components/common/tour/TourProvider";
import { useApp } from "./context/AppContextProvider";
import {
  ensureUsableAccessToken,
  removeAxiosInterceptor,
  setAxiosInterceptor,
} from "./utilization/AxiosUtilization";
import {
  isTokenAboutToExpire,
  isTokenExpired,
} from "./utilization/JwtUtilization";
import useTabActive from "./hooks/useTabActive";
import {
  retrieveAccessTokenFromRefreshToken,
  retrieveUserProfileTokenInformation,
  retrieveUserInformationFromToken,
  retrieveUserProfileDTOById,
} from "./requests/ManagementRequests";
import { NotificationProvider } from "./notifications/NotificationProvider";
import connection_production from "./config/connection-production.json";
import connection_development from "./config/connection-development.json";
import connection_local from "./config/connection-local.json";
import { useMutationObserver } from "./hooks/useMutationObserver";
import i18n from "./language/i18n";
import { queryEarnedAchievementDTOListByParams } from "./requests/GamificationRequests";
import {
  BaseModal,
  constructBaseNumericIndividualModel,
  constructBaseVerbalIndvidualModel,
  findAISettingsFromCurrentProfile,
  FullCard,
  SecondaryButton,
  sharedAnimations,
  libraryStyles,
} from "./library";
import { componentStyles } from "./components/styles/componentStyles";
import NewEarnedAchievementCelebration from "./components/common/achievements/NewEarnedAchievementCelebration";
import {} from "./requests/CatchtivityRequests";
import { queryIndividualModalByParams } from "./requests/AIModelConstructorRequests";
import { querySubjectDescriptionCompletionListByUserIdAndUserProfileId } from "./requests/CategoryRequests";
import { useWebSocket } from "./hooks/useWebSocket";
import ImpersonationBanner from "./components/common/ImpersonationBanner";
import AiRefusalNotice from "./components/common/AiRefusalNotice";
import {
  forgetDeactivatedProfile,
  isLearnerProfileDeactivatedError,
  rememberDeactivatedProfile,
} from "./utilization/DeactivatedProfileUtilization";
import {
  clearImpersonationState,
  isImpersonatingBySession,
  isImpersonationStale,
  readImpersonationState,
} from "./utilization/ImpersonationSessionUtilization";
import {
  detectStorePlatform,
  isMobileAppHandoffRoute,
  isMobileAppSsoHandoffEnabled,
  storeUrlFor,
} from "./utilization/MobileAppHandoffUtilization";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

// 423 = the refresh token was revoked (a password change ends every session) — go to login. Falling
// through to SERVER_UNREACHABLE showed "güncelleme yapıyoruz, 15 dakika bekleyin" on every reload,
// forever, because the dead refresh token stayed in localStorage.
const isRevokedSessionError = (err) => err?.response?.status === 423;

const App = (props) => {
  const {
    boarding,
    setBoarding,
    theme,
    setTheme,
    mainLeftNavigationState,
    setMainLeftNavigationState,
  } = useApp();
  const {
    language,
    handleStateLogin,
    handleStateLogout,
    handleStateChooseProfile,
    handleStateChangeProfile,
    userInformation,
    userProfile,
    webSocketStompClient,
    webSocketConnectionStatus,
    newEarnedAchievementDTO,
    newEarnedAchievementDTOList,
    mascotInventoryItemDTOList,
    learnerDataRefreshCounter,
    setLanguage,
    setUserInformation,
    setEarnedAchievementListLoading,
    setEarnedAchievementDTOList,
    setNewEarnedAchievementDTO,
    setNewEarnedAchievementDTOList,
    setIndividualModelListLoading,
    setIndividualModelDTOList,
    setSubjectDescriptionCompletionListLoading,
    setSubjectDescriptionCompletionDTOList,
    setMascotChatCanConvertAudio,
    setMascotHasVoiceItem,
    setMascotChatVoiceType,
    setMascotChatActType,
  } = useAuth();
  const { disconnect: disconnectWebSocket, connect: connectWebSocket } =
    useWebSocket();
  const isTabActive = useTabActive();
  const [latestUpdateTime, setLatestUpdateTime] = useState(
    new Date().getTime(),
  );
  const [
    showNewEarnedAchievementDTOListModal,
    setShowNewEarnedAchievementDTOListModal,
  ] = useState(false);
  const mutationRef = useRef();
  const [isMobile, setIsMobile] = useState(false);

  // "Yeniden bağlan" (SessionInactiveModal) → reopen the websocket. Mirrors admin-web: the modal
  // dispatches a window event rather than importing the connection, so App stays the single owner of
  // the socket lifecycle and no second `useWebSocket()` instance is created.
  //
  // Routed through a ref on purpose. `connectWebSocket` is a useCallback over [userInformation,
  // userProfile, …], and a listener registered once with `[]` deps would capture the FIRST one —
  // built at mount, when both are still null — whose own guard (`if (!userInformation ||
  // !userProfile) return`) makes every retry a silent no-op. (That is exactly the state admin-web's
  // avatar-menu retry was in.) The ref keeps the listener registered once while always calling the
  // CURRENT connect.
  const connectWebSocketRef = useRef(connectWebSocket);
  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);
  useEffect(() => {
    const onRetry = async () => {
      // MINT a token when the stored one is stale instead of giving up on it. The old guard
      // (`accessToken && !isTokenExpired(accessToken)`) made the modal's only button a silent no-op
      // on any tab whose access token had aged out — the status never moved, so nothing in the modal,
      // the console or the connection dot changed, and the learner was left closing an overlay by
      // hand. How often that bites depends on how long the tab has been open (the access token
      // outlives the 30-minute reap, so the token is frequently still valid and the old guard did
      // work), which is exactly why it read as intermittent rather than broken. Minting removes the
      // dependence on that timing altogether. HTTP kept working throughout because the axios
      // interceptor refreshes on its own — the websocket CONNECT frame is the one call that carries
      // its bearer itself.
      const accessToken = await ensureUsableAccessToken();
      // Still nothing usable (no refresh token, or it is expired too) => do NOT open a socket the
      // server will only reject. SessionInactiveModal resolves on its own deadline either way.
      if (!accessToken) return;
      // bypassTabCheck: `connectWebSocket`'s tab guard is there to stop BACKGROUND tabs from
      // reconnecting, not to veto an explicit user action — and after the await above we are no
      // longer in the click's tick, so an intervening visibility change could veto the very retry the
      // learner just asked for.
      connectWebSocketRef.current(accessToken, true);
    };
    window.addEventListener("retryWebSocket", onRetry);
    return () => window.removeEventListener("retryWebSocket", onRetry);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const { pathname } = window.location;
      // The SSO landing routes render their own phone "continue in the app" card (K12 always; SEBIT and
      // external-client once the app handles them), and /login/with-k12 is also the page the mobile app's own K12
      // sign-in returns through — none of these may be covered by this modal.
      if (
        pathname.startsWith("/upload/file-to-application") ||
        pathname.startsWith("/solve/activity") ||
        isMobileAppHandoffRoute(pathname, isMobileAppSsoHandoffEnabled())
      ) {
        setIsMobile(false);
        return;
      }
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleAppStateEventListener = () => {
      const localStorageAppState = localStorage.getItem("appState");
      if (
        localStorageAppState === "LOGIN" ||
        localStorageAppState === "LOGOUT" ||
        localStorageAppState === "CHANGE_PROFILE"
      ) {
        localStorage.removeItem("appState");
        if (localStorageAppState === "CHANGE_PROFILE") {
          const userProfileId = parseFloat(
            localStorage.getItem("userProfileId"),
          );
          if (userProfile) {
            if (userProfileId !== userProfile.id) {
              window.location.replace(connection.app.url);
            }
          } else {
            window.location.replace(connection.app.url);
          }
        } else {
          window.location.replace(connection.app.url);
        }
      }
    };
    window.addEventListener("storage", handleAppStateEventListener);
    return () => {
      window.removeEventListener("storage", handleAppStateEventListener);
    };
  }, [userProfile]);

  useEffect(() => {
    const handleServerUnreachable = () => {
      setBoarding("SERVER_UNREACHABLE");
    };
    window.addEventListener("serverUnreachable", handleServerUnreachable);
    return () => {
      window.removeEventListener("serverUnreachable", handleServerUnreachable);
    };
  }, []);

  useEffect(() => {
    const retrieveAndSetMainLeftNavigationState = () => {
      const foundMainLeftNavigationState = localStorage.getItem(
        "mainLeftNavigationState",
      );
      if (foundMainLeftNavigationState) {
        if (
          foundMainLeftNavigationState === "FULL" ||
          foundMainLeftNavigationState === "ICON_ONLY"
        ) {
          setMainLeftNavigationState(foundMainLeftNavigationState);
        } else {
          setMainLeftNavigationState("FULL");
        }
      }
    };
    const retrieveAndSetTheme = () => {
      const foundTheme = localStorage.getItem("theme");
      setTheme(foundTheme);
    };
    const retrieveAndSetUserInformation = async () => {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        const { data, err } =
          await retrieveUserInformationFromToken(accessToken);
        if (err) {
          if (
            err.response &&
            (err.response.status === 400 || err.response.status === 401)
          ) {
            setBoarding("TOKEN_ERROR");
          } else if (err.response && err.response.status === 404) {
            setBoarding("NETWORK_ERROR");
          } else if (isRevokedSessionError(err)) {
            handleLogoutOnClick();
          } else {
            setBoarding("SERVER_UNREACHABLE");
          }
          return;
        }
        handleStateLogin(accessToken, null, data, null);
        setUserInformation(data);
        setBoarding("USER_RETRIEVED");
      } else {
        handleLogoutOnClick();
        setBoarding("USER_NOT_RETRIEVED");
      }
    };
    const retrieveAndSetOnlyUserProfileInformation = async () => {
      handleStateChangeProfile();
      setBoarding("PROFILE_SELECTION_REQUIRED");
    };
    const retrieveAndSetUserProfileAndUserInformation = async () => {
      try {
        const foundUserProfileId = localStorage.getItem("userProfileId");
        const { data: foundUserProfile, err: profileErr } =
          await retrieveUserProfileDTOById(foundUserProfileId);
        if (profileErr || !foundUserProfile) {
          if (isRevokedSessionError(profileErr)) {
            handleLogoutOnClick();
            return;
          }
          if (
            profileErr &&
            (!profileErr.response ||
              (profileErr.response.status !== 400 &&
                profileErr.response.status !== 401 &&
                profileErr.response.status !== 403 &&
                profileErr.response.status !== 404))
          ) {
            setBoarding("SERVER_UNREACHABLE");
            return;
          }
          await retrieveAndSetUserInformation();
          return;
        }
        const { userDTO } = foundUserProfile;
        if (!userDTO) {
          await retrieveAndSetUserInformation();
          return;
        }
        handleStateLogin(null, null, userDTO, null);
        setUserInformation(userDTO);
        const { enabled, requiredAction } = userDTO;
        if (!enabled) {
          setBoarding("ACCOUNT_ACTIVATION_REQUIRED");
          return;
        }
        if (requiredAction === "LOGOUT") {
          handleLogoutOnClick();
          return;
        }
        if (requiredAction === "PROFILE_SELECTION") {
          handleStateChangeProfile();
          setBoarding("PROFILE_SELECTION_REQUIRED");
          return;
        }
        if (foundUserProfile.deleted) {
          handleStateChangeProfile();
          setBoarding("PROFILE_SELECTION_REQUIRED");
          return;
        }
        const refreshToken = localStorage.getItem("refreshToken");
        const { data: userProfileTokenData, err: tokenErr } =
          await retrieveUserProfileTokenInformation(
            foundUserProfileId,
            refreshToken,
          );
        if (tokenErr) {
          if (isRevokedSessionError(tokenErr)) {
            handleLogoutOnClick();
            return;
          }
          // Pasif öğrenci: deactivated while signed in (or since the last visit) — the explanation screen, not
          // the picker, which would only refuse the same profile again.
          if (isLearnerProfileDeactivatedError(tokenErr)) {
            rememberDeactivatedProfile(foundUserProfile);
            setBoarding("PROFILE_INACTIVE");
            return;
          }
          if (
            !tokenErr.response ||
            (tokenErr.response.status !== 400 &&
              tokenErr.response.status !== 401 &&
              tokenErr.response.status !== 403 &&
              tokenErr.response.status !== 404)
          ) {
            setBoarding("SERVER_UNREACHABLE");
            return;
          }
          handleStateChangeProfile();
          setBoarding("PROFILE_SELECTION_REQUIRED");
          return;
        }
        handleStateLogin(
          userProfileTokenData.accessToken,
          userProfileTokenData.refreshToken,
          null,
          null,
        );
        if (foundUserProfile.role === "LEARNER") {
          const { branchDTO } = foundUserProfile;
          const seasonId = localStorage.getItem("seasonId");
          const gradeId = localStorage.getItem("gradeId");
          const branchId = localStorage.getItem("branchId");
          if (seasonId && gradeId && branchId) {
            if (!branchDTO) {
              handleStateChangeProfile();
              setBoarding("PROFILE_SELECTION_REQUIRED");
              return;
            }
          } else {
            handleStateChangeProfile();
            setBoarding("PROFILE_SELECTION_REQUIRED");
            return;
          }
          const { gradeDTO } = branchDTO;
          const { seasonDTO } = gradeDTO;
          if (seasonDTO.endAt < new Date().getTime()) {
            handleStateChangeProfile();
            setBoarding("PROFILE_SELECTION_REQUIRED");
            return;
          }
          handleStateChooseProfile(
            foundUserProfile,
            seasonDTO,
            gradeDTO,
            branchDTO,
          );
        } else {
          // Any role this app no longer serves — a stale INDIVIDUAL id persisted in localStorage across
          // the deploy, or STAFF/PARENT/CONTENT_CREATOR, which never had a shell here either. Without
          // this arm the token minted above is kept, handleStateChooseProfile never runs so context
          // userProfile stays null, and boarding still flips to DASHBOARD — rendering <Routes> with no
          // child routes: a blank pane holding a live credential, with no route back to the picker or
          // to logout. Drop the profile scope so the next reload cannot re-enter, and fall back to the
          // picker, which is where every other rejection in this function already goes.
          handleStateChangeProfile();
          setBoarding("PROFILE_SELECTION_REQUIRED");
          return;
        }
        setBoarding("DASHBOARD");
      } catch (err) {
        if (isRevokedSessionError(err)) {
          handleLogoutOnClick();
        } else if (
          err &&
          (!err.response ||
            (err.response.status !== 400 &&
              err.response.status !== 401 &&
              err.response.status !== 403 &&
              err.response.status !== 404))
        ) {
          setBoarding("SERVER_UNREACHABLE");
        } else {
          await retrieveAndSetUserInformation();
        }
      }
    };
    const retrieveAndSetIndividualModelDTOList = async () => {
      // AI model-constructor service call disabled — pending CA + PA continuous-training
      // rebuild. Short-circuit to the base verbal + numeric models so the PA template-picker
      // still has something to bias against. Re-enable by un-commenting the block below.
      setIndividualModelDTOList([
        constructBaseVerbalIndvidualModel(userInformation.id),
        constructBaseNumericIndividualModel(userInformation.id),
      ]);
      setIndividualModelListLoading(false);
      return;
      /*
      const { data, err } = await queryIndividualModalByParams({
        userId: userInformation.id,
      });
      if (err) {
        console.log(err);
        setIndividualModelDTOList([
          constructBaseVerbalIndvidualModel(userInformation.id),
          constructBaseNumericIndividualModel(userInformation.id),
        ]);
        setIndividualModelListLoading(false);
        return;
      }
      if (data.length === 0) {
        setIndividualModelDTOList([
          constructBaseVerbalIndvidualModel(userInformation.id),
          constructBaseNumericIndividualModel(userInformation.id),
        ]);
      } else {
        const currentData = [];
        const foundVerbal = data.find((item) => item.coterieField === "VERBAL");
        if (foundVerbal) {
          currentData.push(foundVerbal);
        } else {
          currentData.push(
            constructBaseVerbalIndvidualModel(userInformation.id),
          );
        }
        const foundNumeric = data.find(
          (item) => item.coterieField === "NUMERIC",
        );
        if (foundNumeric) {
          currentData.push(foundNumeric);
        } else {
          currentData.push(
            constructBaseNumericIndividualModel(userInformation.id),
          );
        }
        setIndividualModelDTOList(currentData);
      }
      setIndividualModelListLoading(false);
      */
    };
    const retrieveAndSetSubjectDescriptionCompletionDTOList = async () => {
      setSubjectDescriptionCompletionListLoading(true);
      const { data, err } =
        await querySubjectDescriptionCompletionListByUserIdAndUserProfileId({
          userId: userInformation.id,
          userProfileId: userProfile.id,
        });

      if (err) {
        return;
      }
      setSubjectDescriptionCompletionDTOList(data);
      setSubjectDescriptionCompletionListLoading(false);
    };
    if (boarding === "INITIALIZED") {
      removeAxiosInterceptor();
      if (window.location.pathname.startsWith("/preview/activity/")) {
        setBoarding("LANDING");
      } else if (window.location.pathname.startsWith("/login/with-")) {
        handleLogoutOnClick();
      } else {
        retrieveAndSetAccessTokenInformationFromRefreshToken(true);
      }
      retrieveAndSetMainLeftNavigationState();
      retrieveAndSetTheme();
    } else if (boarding === "PROFILE_SELECTION_REQUIRED") {
    } else if (boarding === "TOKEN_RETRIEVED") {
      const profileId = localStorage.getItem("userProfileId");
      if (profileId) {
        retrieveAndSetUserProfileAndUserInformation();
      } else {
        retrieveAndSetUserInformation();
      }
    } else if (
      boarding === "USER_RETRIEVED" ||
      boarding === "USER_NOT_RETRIEVED"
    ) {
      if (userInformation) {
        const { enabled, requiredAction } = userInformation;
        if (enabled) {
          if (requiredAction === "LOGOUT") {
            handleLogoutOnClick();
          } else if (requiredAction === "PROFILE_SELECTION") {
            retrieveAndSetOnlyUserProfileInformation();
          } else {
            handleStateChangeProfile();
            setBoarding("PROFILE_SELECTION_REQUIRED");
          }
        } else {
          setBoarding("ACCOUNT_ACTIVATION_REQUIRED");
        }
      } else {
        setBoarding("LANDING");
      }
    } else if (boarding === "DASHBOARD") {
      const { pathname } = window.location;
      if (pathname.startsWith("/upload/file-to-application")) return;
      if (pathname.startsWith("/solve/activity")) return;
      // LearnerApp's two bootstrap aggregates already cover the per-learner reads (personalized
      // assignments, wrong activities, activity reviews, personalStatus, earned achievements,
      // market purchases, mascot inventory) in the same requests, so nothing standalone is fetched
      // here. The block that did was for the individual profile, which no longer exists.
      retrieveAndSetIndividualModelDTOList();
      retrieveAndSetSubjectDescriptionCompletionDTOList();
    }
  }, [boarding, learnerDataRefreshCounter]);

  useEffect(() => {
    if (!userInformation || !userProfile || boarding !== "DASHBOARD") {
      if (
        webSocketConnectionStatus === "connected" ||
        webSocketConnectionStatus === "connecting"
      ) {
        disconnectWebSocket();
      }
    }
  }, [
    userInformation,
    userProfile,
    boarding,
    webSocketConnectionStatus,
    disconnectWebSocket,
  ]);

  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, []);

  useEffect(() => {
    if (!webSocketStompClient || !userProfile) return;
    const subscribed = webSocketStompClient.subscribe(
      `/${userProfile.id}/gamification/new-earned-achievement`,
      (message) => {
        let { body } = message;
        body = JSON.parse(body);
        const { level, ...items } = body;
        // `achievementDTO` here mirrors the backend earned-achievement JSON object shape.
        body = { level: parseFloat(level), achievementDTO: { ...items } };
        setNewEarnedAchievementDTO(body);
        retrieveAndSetEarnedAchievementList();
      },
    );
    return () => {
      subscribed.unsubscribe();
    };
  }, [webSocketStompClient, userProfile]);

  useEffect(() => {
    if (!newEarnedAchievementDTO) return;
    // Append via a functional updater against the LATEST list, not the render-closure copy: achievements
    // arrive one-by-one over the websocket and a single action can award several back-to-back, so a
    // stale-closure spread could drop one. Keeping prior references stable (no deep clone) means a
    // late arrival only adds a slide instead of re-rendering/re-animating the achievement being celebrated.
    // Dedupe on family + level so a STOMP redelivery / reconnect replay can't add a duplicate slide.
    const achievementKey = (earned: any) =>
      `${earned.achievementDTO.coterieType}|${earned.achievementDTO.sourceType}|${earned.achievementDTO.trackingType}|${earned.level}`;
    setNewEarnedAchievementDTOList((prev: any[]) => {
      const key = achievementKey(newEarnedAchievementDTO);
      if (prev.some((earned) => achievementKey(earned) === key)) return prev;
      return [...prev, newEarnedAchievementDTO];
    });
  }, [newEarnedAchievementDTO]);

  useEffect(() => {
    if (newEarnedAchievementDTOList.length === 0) {
      setShowNewEarnedAchievementDTOListModal(false);
    } else {
      setShowNewEarnedAchievementDTOListModal(true);
    }
  }, [newEarnedAchievementDTOList]);

  useEffect(() => {
    if (theme === "DARK") {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "DARK");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "LIGHT");
    }
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("mainLeftNavigationState", mainLeftNavigationState);
  }, [mainLeftNavigationState]);

  useEffect(() => {
    setLanguage(language);
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
    document.documentElement.lang = language.toLowerCase();
  }, [language]);

  useEffect(() => {
    if (!userInformation) return;
    if (!userProfile) return;
    if (userProfile.role === "LEARNER") {
      // Voice mode is governed by the org AI settings (institution ?? campus ?? brand).
      const institutionDTO =
        userProfile.branchDTO?.gradeDTO?.seasonDTO?.institutionDTO;
      const campusDTO = institutionDTO?.campusDTO;
      const brandDTO = campusDTO?.brandDTO;
      const { canUseMascotVoiceChat, canDrawChatDiagram } =
        findAISettingsFromCurrentProfile(brandDTO, campusDTO, institutionDTO);
      setMascotChatCanConvertAudio(canUseMascotVoiceChat);
      // Institution-managed policy for AI-drawn chat diagrams (admin-web AI settings). The chat
      // request layer (AIRequests) is non-React and reads this from localStorage; supportsDiagram
      // sent to the backend = this policy AND the client can render (a web client always can).
      localStorage.setItem(
        "aiCanDrawChatDiagram",
        String(canDrawChatDiagram === true),
      );

      const foundMascotInventoryItemDTO_VOICE = mascotInventoryItemDTOList.find(
        (mascotInventoryItemDTO) =>
          mascotInventoryItemDTO.marketItemDTO.type === "VOICE",
      );
      if (foundMascotInventoryItemDTO_VOICE) {
        const { marketItemDTO } = foundMascotInventoryItemDTO_VOICE;
        const { name } = marketItemDTO;
        setMascotChatVoiceType(name.replaceAll("-", "_").toUpperCase());
        setMascotHasVoiceItem(true);
      } else {
        setMascotHasVoiceItem(false);
      }
      const foundMascotInventoryItemDTO_ACT = mascotInventoryItemDTOList.find(
        (mascotInventoryItemDTO) =>
          mascotInventoryItemDTO.marketItemDTO.type === "ACT",
      );
      if (foundMascotInventoryItemDTO_ACT) {
        const { marketItemDTO } = foundMascotInventoryItemDTO_ACT;
        const { name } = marketItemDTO;
        setMascotChatActType(name.replaceAll("-", "_").toUpperCase());
      } else {
        setMascotChatActType("DEFAULT_ACT");
      }
    }
  }, [userInformation, userProfile, mascotInventoryItemDTOList]);

  useMutationObserver(mutationRef, () => {
    const elementList = document.getElementsByClassName("katex-mathml");
    for (const element of elementList) {
      (element as HTMLElement).style.display = "none";
    }
  });

  const retrieveAndSetAccessTokenInformationFromRefreshToken = async (
    changeBoarding,
  ) => {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (accessToken) {
      if (
        refreshToken &&
        !isTokenAboutToExpire(accessToken) &&
        !isTokenAboutToExpire(refreshToken, 86400000)
      ) {
        removeAxiosInterceptor();
        // setAxiosInterceptor takes 0 args; the previous accessToken arg was always silently dropped.
        setAxiosInterceptor();
        if (changeBoarding) {
          setBoarding("TOKEN_RETRIEVED");
        }
        return;
      }
      removeAxiosInterceptor();
      // setAxiosInterceptor takes 0 args; the previous accessToken arg was always silently dropped.
      setAxiosInterceptor();
      if (refreshToken) {
        const { data, err } =
          await retrieveAccessTokenFromRefreshToken(refreshToken);
        removeAxiosInterceptor();
        if (err) {
          // Pasif öğrenci: the refresh is refused with the same key as the pick — the usual way a learner meets a
          // deactivation that happened since the last visit (the access token has expired by then).
          if (isLearnerProfileDeactivatedError(err)) {
            forgetDeactivatedProfile();
            setBoarding("PROFILE_INACTIVE");
          } else if (
            err.response &&
            (err.response.status === 400 || err.response.status === 401)
          ) {
            setBoarding("TOKEN_ERROR");
          } else if (err.response && err.response.status === 404) {
            setBoarding("NETWORK_ERROR");
          } else if (isRevokedSessionError(err)) {
            handleLogoutOnClick();
          } else {
            setBoarding("SERVER_UNREACHABLE");
          }
        } else {
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
            data;
          localStorage.setItem("accessToken", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);
          setAxiosInterceptor();

          if (changeBoarding) {
            setBoarding("TOKEN_RETRIEVED");
          }
        }
      } else if (isImpersonatingBySession()) {
        // An impersonated session carries NO refresh token on purpose — that absence is the only thing
        // bounding it, since nothing here revokes a JWT. Falling through to LANDING left the impersonated
        // token (the admin's `act` claim over the learner's identity) sitting in localStorage on a shared
        // origin for the rest of its life, with no session driving it and no audit close ever fired: the
        // trail recorded a session that, as far as the log knew, never ended.
        if (!isTokenExpired(accessToken)) {
          // Still valid — rehydrate it rather than dropping a live audited session on a reload.
          setAxiosInterceptor();
          if (changeBoarding) {
            setBoarding("TOKEN_RETRIEVED");
          }
        } else {
          // Expired: tear it down properly so nothing is orphaned.
          handleStateLogout();
          clearImpersonationState();
          setBoarding("LANDING");
        }
      } else {
        setBoarding("LANDING");
      }
    } else {
      setBoarding("LANDING");
    }
  };

  const retrieveAndSetEarnedAchievementList = async () => {
    setEarnedAchievementListLoading(true);
    const { data, err } = await queryEarnedAchievementDTOListByParams({
      userId: userInformation.id,
      userProfileId: userProfile.id,
    });
    if (err) {
      return;
    }
    setEarnedAchievementDTOList(data);
    setEarnedAchievementListLoading(false);
  };

  const handleLogoutOnClick = () => {
    handleStateLogout();
    setBoarding("LANDING");
  };

  const RenderShowNewEarnedAchievementDTOModal = () => {
    const closeNewEarnedAchievementModal = () => {
      setShowNewEarnedAchievementDTOListModal(false);
      setNewEarnedAchievementDTO(null);
      setNewEarnedAchievementDTOList([]);
    };
    return (
      <BaseModal
        title={
          newEarnedAchievementDTOList.length > 1
            ? i18n.t("new_earned_achievements")
            : i18n.t("new_earned_achievement")
        }
        icon="🎉"
        size="medium"
        isOpen={showNewEarnedAchievementDTOListModal}
        onAfterOpen={() => {}}
        onRequestClose={closeNewEarnedAchievementModal}
      >
        <NewEarnedAchievementCelebration
          earnedAchievementDTOList={newEarnedAchievementDTOList}
          onClose={closeNewEarnedAchievementModal}
        />
      </BaseModal>
    );
  };

  const RenderMobileDeviceNotSupportedModal = () => {
    // A narrow desktop window also trips `isMobile`; only devices the app runs on get a store link.
    const phonePlatform = detectStorePlatform(
      window.navigator.userAgent,
      window.navigator.maxTouchPoints
    );
    return (
      <BaseModal
        title={i18n.t("device_not_supported")}
        icon="📱"
        size="small"
        isOpen={isMobile}
        onAfterOpen={() => {}}
        onRequestClose={() => {}}
      >
        <div className="ap-device-modal">
          <p className="ap-device-title">{i18n.t("please_use_tablet_or_pc")}</p>
          <p className="ap-device-desc">
            {i18n.t("mobile_not_supported_description")}
          </p>
          {phonePlatform ? (
            <SecondaryButton
              title={i18n.t(
                phonePlatform === "ios"
                  ? "mobile_app_download_ios"
                  : "mobile_app_download_android"
              )}
              size="unlimited"
              onClick={() => {
                window.location.href = storeUrlFor(phonePlatform);
              }}
            />
          ) : null}
        </div>
      </BaseModal>
    );
  };

  let role = null;
  if (userProfile) {
    ({ role } = userProfile);
  }

  // Abandoned-impersonation guard. A tab closed without pressing "Yöneticiye dön" leaves the flags behind
  // indefinitely; past the max age they belong to a session nobody can still be in, so drop them rather than
  // draw a banner over an ordinary learner's app.
  useEffect(() => {
    const { active, startedAt } = readImpersonationState();
    if (active && isImpersonationStale(Date.now(), startedAt)) {
      clearImpersonationState();
    }
  }, []);

  return (
    <div
      className="ap-root"
      onClick={() => {
        setLatestUpdateTime(new Date().getTime());
      }}
      ref={mutationRef}
    >
      <style>{sharedAnimations}</style>
      <style>{libraryStyles}</style>
      <style>{componentStyles}</style>
      <NotificationProvider />
      {/* Mounted here, above the role branch below, so the "viewing as X" band renders under EVERY boarding
          state and every route — including the LearnerApp shell and the login screen an
          expired impersonation token lands on. It returns null when no impersonation is active. */}
      <ImpersonationBanner />
      {/* Mounted beside the impersonation band, and for the same reason: a refused AI action has to be
          explainable from every route and every boarding state. It renders nothing until something is
          actually refused, and the two bands stack in CSS rather than each padding the body. */}
      <AiRefusalNotice />
      {RenderShowNewEarnedAchievementDTOModal()}
      {RenderMobileDeviceNotSupportedModal()}
      <TourProvider>
        {role === "LEARNER" ? (
          <LearnerApp>{props.children}</LearnerApp>
        ) : (
          <div>{props.children}</div>
        )}
      </TourProvider>
    </div>
  );
};

export default App;
